﻿<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { RecycleScroller } from 'vue-virtual-scroller'
import type { ImageMetaRecord } from '../types/catalog'
import { formatBytes, formatDate } from '../utils/format'
import AppIcon from './AppIcon.vue'
import ImageThumbnail from './ImageThumbnail.vue'

const props = defineProps<{
  images: ImageMetaRecord[]
  loading: boolean
  hasMore: boolean
}>()

const emit = defineEmits<{
  loadMore: []
  edit: [image: ImageMetaRecord]
  remove: [image: ImageMetaRecord]
}>()

const host = ref<HTMLElement | null>(null)
const width = ref(360)
let resizeObserver: ResizeObserver | null = null

const columns = computed(() => {
  const minimum = width.value < 620 ? 168 : 220
  return Math.max(1, Math.floor(width.value / minimum))
})
const columnWidth = computed(() => Math.floor(width.value / columns.value))
const rowHeight = computed(() => width.value < 620 ? 250 : 292)

onMounted(() => {
  if (!host.value) return
  resizeObserver = new ResizeObserver(([entry]) => {
    width.value = Math.max(280, Math.floor(entry.contentRect.width))
  })
  resizeObserver.observe(host.value)
})

onBeforeUnmount(() => resizeObserver?.disconnect())
</script>

<template>
  <section ref="host" class="gallery-host" aria-live="polite">
    <div v-if="!images.length && !loading" class="empty-state">
      <div class="empty-state-icon"><AppIcon name="image" :size="34" /></div>
      <h3>No images here</h3>
      <p>Import a photo or use the camera to start cataloging.</p>
    </div>

    <RecycleScroller
      v-else
      class="gallery-scroller"
      :items="images"
      :item-size="rowHeight"
      :grid-items="columns"
      :item-secondary-size="columnWidth"
      key-field="id"
      :buffer="900"
      @scroll-end="emit('loadMore')"
    >
      <template #default="{ item }">
        <div class="gallery-cell">
          <article class="image-card" @dblclick="emit('edit', item)">
            <button class="image-card-media" type="button" :aria-label="`Edit ${item.title}`" @click="emit('edit', item)">
              <ImageThumbnail :image-id="item.id" :alt="item.title" />
              <span class="image-dimensions">{{ item.width }} × {{ item.height }}</span>
            </button>
            <div class="image-card-content">
              <div class="image-card-heading">
                <div class="image-card-copy">
                  <h3 :title="item.title">{{ item.title }}</h3>
                   <!--  
                   <p>{{ formatDate(item.updatedAt) }} · {{ formatBytes(item.size) }}</p>
                   -->
                   <p>{{ formatDate(item.updatedAt) }}</p>
                </div>
                <div class="image-card-actions">
                  <button type="button" class="mini-icon-button" aria-label="Edit image" @click="emit('edit', item)">
                    <AppIcon name="edit" :size="16" />
                  </button>
                  <button type="button" class="mini-icon-button mini-icon-button--danger" aria-label="Delete image" @click="emit('remove', item)">
                    <AppIcon name="trash" :size="16" />
                  </button>
                </div>
              </div>
              <p v-if="item.description" class="image-description">{{ item.description }}</p>
              <div v-if="item.tags.length" class="tag-list" aria-label="Tag">
                <span v-for="tag in item.tags.slice(0, 3)" :key="tag" class="tag-chip">#{{ tag }}</span>
                <span v-if="item.tags.length > 3" class="tag-chip tag-chip--muted">+{{ item.tags.length - 3 }}</span>
              </div>
            </div>
          </article>
        </div>
      </template>
      <template #after>
        <div class="gallery-after">
          <span v-if="loading" class="spinner" aria-label="Loading images" />
          <button v-else-if="hasMore" class="text-button" type="button" @click="emit('loadMore')">Load more images</button>
        </div>
      </template>
    </RecycleScroller>
  </section>
</template>
